import { type CommunitySlim, createCommunitySlim } from '../../community';
import type {
  CommunityContentObjectDetailDataObject,
  CommunityContentObjectListDataObject,
  PublicCommunityContentObjectDataObject,
} from '../../service';
import { type UploadedFile, createUploadedFile } from '../../uploaded-file';
import type { ContentObjectDetail } from '../content-object-detail';
import type { ContentObjectList } from '../content-object-list';
import { type OrganizationContentObject, createOrganizationContentObject } from './organization-content-object';

export type CommunityContentObject =
  | Extract<ContentObjectDetail, { contentableType: 'Community' }>
  | Extract<ContentObjectList, { contentableType: 'Community' }>;

export type CommunityContentObjectDetail = {
  contentableType: 'Community';
  postInCommunities: [CommunitySlim];
} & OrganizationContentObject;

export type CommunityContentObjectList = {
  contentableType: 'Community';
  postInCommunities: [CommunitySlim];
} & OrganizationContentObject;

export const isCommunityContentObject = (parameters: {
  contentObject: ContentObjectDetail | ContentObjectList;
}): parameters is { contentObject: CommunityContentObject } => parameters.contentObject.contentableType === 'Community';

export const createCommunityContentObjectDetail = (
  data: CommunityContentObjectDetailDataObject,
): CommunityContentObjectDetail => {
  const [community] = data.post_in_communities;

  if (!community) {
    throw new Error('Missing post_in_communities entry');
  }

  return {
    ...createOrganizationContentObject(data),
    contentableType: data.contentable_type,
    postInCommunities: [createCommunitySlim(community)],
  };
};

/* eslint-disable sonarjs/no-identical-functions -- Detail and List factories are intentionally symmetric; their DataObject shapes are expected to diverge */
export const createCommunityContentObjectList = (
  data: CommunityContentObjectListDataObject,
): CommunityContentObjectList => {
  const [community] = data.post_in_communities;

  if (!community) {
    throw new Error('Missing post_in_communities entry');
  }

  return {
    ...createOrganizationContentObject(data),
    contentableType: data.contentable_type,
    postInCommunities: [createCommunitySlim(community)],
  };
};
/* eslint-enable sonarjs/no-identical-functions -- end of symmetric factory */

export type PublicCommunityContentObject = { attachments: UploadedFile[]; contentableType: 'Community' };

export const createPublicCommunityContentObject = (
  data: PublicCommunityContentObjectDataObject,
): PublicCommunityContentObject => ({
  attachments: data.attachments.map((attachment) => createUploadedFile(attachment)),
  contentableType: data.contentable_type,
});
